/**
 * Sentry submission adapter for the event capture report modal.
 * Does not handle Sentry initialisation — accepts an already-initialised instance.
 */

/**
 * Convert a base64 PNG data URL to a Uint8Array.
 * Exported for testing.
 */
export function dataUrlToUint8Array(dataUrl) {
  const prefix = 'data:image/png;base64,'
  if (typeof dataUrl !== 'string' || !dataUrl.startsWith(prefix)) {
    throw new Error('Invalid PNG data URL')
  }

  const base64 = dataUrl.slice(prefix.length)
  if (!base64) {
    throw new Error('Invalid PNG data URL')
  }

  const binaryString = atob(base64)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.codePointAt(i)
  }
  return bytes
}

/**
 * Build a descriptive Sentry message from the report context.
 * Uses the most recent error in the event buffer, then falls back to the page URL.
 */
export function buildSentryMessage({ message, events = [], pageUrl } = {}) {
  if (message) return `User report: ${message}`

  const lastError = [...events].reverse().find((e) => e.type === 'error')
  if (lastError) {
    const description = lastError.message || lastError.reason?.message || lastError.reason
    if (description) return `User report: ${description}`
  }

  const location = pageUrl || (typeof window !== 'undefined' ? window.location.href : '')
  return location ? `User report from ${location}` : 'User submitted event capture report'
}

/**
 * Create a Sentry submission adapter.
 *
 * @param {object} sentryInstance - An already-initialised Sentry instance (e.g. `import * as Sentry from '@sentry/browser'`)
 * @param {object} [config]
 * @param {string} [config.level='error'] - Sentry severity level: 'fatal'|'error'|'warning'|'info'|'debug'|'log'
 * @param {object} [config.tags={}] - Custom Sentry tags to attach alongside the page URL
 * @returns {Function} Async submit function: ({ events, screenshotDataUrl, message }) => Promise<string|undefined>
 */
export function createSentryAdapter(sentryInstance, config = {}) {
  const { level = 'error', tags = {} } = config

  /**
   * @param {object} report
   * @param {Array}  [report.events=[]]           - Event history from EventCollector
   * @param {string} [report.screenshotDataUrl]   - Optional base64 PNG data URL
   * @param {string} [report.message]             - Optional user-provided description
   * @returns {Promise<string|undefined>} Sentry event ID
   */
  return async function submitToSentry({ events = [], screenshotDataUrl, message, pageUrl } = {}) {
    let eventId

    const sentryMessage = buildSentryMessage({ message, events, pageUrl })

    sentryInstance.withScope((scope) => {
      scope.setLevel(level)

      scope.setTag('page_url', pageUrl || window.location.href)

      Object.entries(tags).forEach(([key, value]) => {
        scope.setTag(key, value)
      })

      scope.setExtra('event_history', events)

      if (screenshotDataUrl && typeof scope.addAttachment === 'function') {
        try {
          scope.addAttachment({
            filename: 'screenshot.png',
            data: dataUrlToUint8Array(screenshotDataUrl),
            contentType: 'image/png',
          })
        } catch {
          // Screenshot attachment silently omitted when SDK does not support it or data is invalid
        }
      }

      eventId = sentryInstance.captureMessage(sentryMessage, level)
    })

    return eventId
  }
}
